import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RecipeSelect } from "@/components/common/entity-select";
import type { Recipe } from "@/types/recipe";
import { useCreateCookingSession } from "@/hooks/cooking-sessions/mutations";
import type { DeckPriority } from "@/types/cooking-session";
import { timerToSeconds } from "@/components/recipes/execution-steps/types";
import type { RecipeStep } from "@/components/recipes/execution-steps/types";
import { cn } from "@/lib/utils";
import { useNow } from "@/lib/elapsed";

function sumStepTimersMinutes(instructions: unknown[] | undefined): number | null {
  if (!instructions?.length) return null;
  const steps = instructions as RecipeStep[];
  const totalSeconds = steps.reduce((acc, step) => {
    if (step.timerEnabled && (step.timer ?? 0) > 0) {
      return acc + timerToSeconds(step.timer!, step.timerUnit);
    }
    return acc;
  }, 0);
  if (totalSeconds === 0) return null;
  return Math.ceil(totalSeconds / 60);
}

/**
 * How long this recipe takes, start to finish (T-1107).
 *
 * The recipe's own numbers first: prep, cook and rest are what the author said
 * it takes, and prep is most of it for the things a deck is used for — a
 * marinade is twenty minutes of work and no timer at all. Step timers are the
 * fallback for a recipe that states no times, and they undercount on purpose,
 * since only timed steps are counted and knife work never is.
 */
export function recipeTargetMinutes(
  recipe: Pick<Recipe, "prepTimeMinutes" | "cookTimeMinutes" | "restTimeMinutes" | "instructions">,
): number | null {
  const stated =
    (recipe.prepTimeMinutes ?? 0) + (recipe.cookTimeMinutes ?? 0) + (recipe.restTimeMinutes ?? 0);
  if (stated > 0) return stated;
  return sumStepTimersMinutes(recipe.instructions);
}

/** When something started now would be ready. */
function readyBy(now: number, minutes: number): string {
  return new Date(now + minutes * 60_000).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

interface AddToDeckDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationId?: number;
}

export function AddToDeckDialog({ open, onOpenChange, locationId }: AddToDeckDialogProps) {
  const { t } = useLingui();
  const [recipeId, setRecipeId] = useState<number | null>(null);
  const [priority, setPriority] = useState<DeckPriority>("NORMAL");
  const [targetMinutes, setTargetMinutes] = useState<string>("");

  const PRIORITIES: { label: string; value: DeckPriority; color: string }[] = [
    { label: t`High`,   value: "HIGH",   color: "bg-(--color-priority-high) text-(--brand-white)" },
    { label: t`Normal`, value: "NORMAL", color: "bg-(--color-warning) text-(--brand-white)" },
    { label: t`Low`,    value: "LOW",    color: "bg-(--gray-400) text-(--brand-white)" },
  ];

  const createSession = useCreateCookingSession();

  function handleRecipeSelect(recipe: Recipe) {
    setRecipeId(recipe.id);
    // Always take the new recipe's answer, including when it has none. Leaving
    // the last recipe's number in the box would show it as this one's.
    const computed = recipeTargetMinutes(recipe);
    setTargetMinutes(computed === null ? "" : String(computed));
  }

  const parsedMinutes = targetMinutes ? parseInt(targetMinutes, 10) : null;
  const invalidTarget = targetMinutes !== "" && (isNaN(parsedMinutes!) || parsedMinutes! < 1);

  // A duration means nothing to someone looking at a clock, so say what it lands
  // on. Ticking rather than frozen at open: a dialog left sitting would
  // otherwise promise a time that has already passed. Not read during render —
  // the compiler treats that as impure.
  const now = useNow(open);

  async function handleSubmit() {
    if (!recipeId || !locationId || invalidTarget) return;
    await createSession.mutateAsync({
      recipeId,
      locationId,
      priority,
      targetMinutes: parsedMinutes ?? undefined,
    });
    onOpenChange(false);
    setRecipeId(null);
    setPriority("NORMAL");
    setTargetMinutes("");
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t`Add to deck`}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-5 pt-2">
          {/* Recipe search */}
          <div className="flex flex-col gap-2">
            <Label>{t`Recipe`}</Label>
            <RecipeSelect
              value={recipeId}
              onChange={(_id, recipe) => handleRecipeSelect(recipe)}
              onClear={() => setRecipeId(null)}
            />
          </div>

          {/* Priority */}
          <div className="flex flex-col gap-2">
            <Label>{t`Priority`}</Label>
            <div className="flex gap-2">
              {PRIORITIES.map((p) => (
                <Button
                  key={p.value}
                  variant="outline"
                  onClick={() => setPriority(p.value)}
                  className={cn(
                    "flex-1 rounded-lg",
                    priority === p.value
                      ? `${p.color} border-transparent`
                      : "hover:bg-(--color-surface-muted)/50",
                  )}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Target time */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="deck-target-minutes">{t`Target time (minutes)`}</Label>
            <Input
              id="deck-target-minutes"
              type="number"
              min={1}
              placeholder={t`e.g. 30`}
              value={targetMinutes}
              onChange={(e) => setTargetMinutes(e.target.value)}
              className={invalidTarget ? "border-(--color-danger) focus-visible:ring-destructive" : ""}
            />
            {invalidTarget ? (
              <p className="text-xs text-(--color-danger-text)">{t`Must be a whole number greater than 0.`}</p>
            ) : parsedMinutes ? (
              <p className="text-xs text-(--color-text-secondary)">
                {t`Ready by ${readyBy(now, parsedMinutes)} if it starts now.`}
              </p>
            ) : null}
          </div>

          {!locationId && (
            <p className="text-sm text-(--color-warning-text) bg-(--color-warning-bg) rounded-lg px-3 py-2">
              {t`Select a location from the header before adding to deck.`}
            </p>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t`Cancel`}
            </Button>
            <Button
              disabled={!recipeId || !locationId || invalidTarget || createSession.isPending}
              onClick={handleSubmit}
            >
              {createSession.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t`Add to deck`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
